import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Logo } from '../Logo/Logo';
import { ChevronIcon, CloseIcon } from '../icons/Icons';
import { NAV_SECTIONS, type NavItem } from './navConfig';
import './Sidebar.css';

interface SidebarProps {
  collapsed: boolean;
  /** Mobile off-canvas open state. */
  mobileOpen: boolean;
  onClose: () => void;
}

function Badge({ label, tone }: NonNullable<NavItem['badge']>) {
  return <span className={`sidebar__badge sidebar__badge--${tone}`}>{label}</span>;
}

function Leaf({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to!}
      end={item.end}
      className={({ isActive }) => (isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link')}
    >
      {Icon ? <Icon className="sidebar__icon" /> : <span className="sidebar__icon sidebar__icon--dot" />}
      <span className="sidebar__label">{item.label}</span>
      {item.badge && <Badge {...item.badge} />}
    </NavLink>
  );
}

function Group({ item }: { item: NavItem }) {
  const [open, setOpen] = useState(true);
  const Icon = item.icon;
  return (
    <div className="sidebar__group">
      <button
        type="button"
        className="sidebar__link sidebar__group-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {Icon && <Icon className="sidebar__icon" />}
        <span className="sidebar__label">{item.label}</span>
        <ChevronIcon className={open ? 'sidebar__chevron sidebar__chevron--open' : 'sidebar__chevron'} />
      </button>
      {open && (
        <div className="sidebar__children">
          {item.children!.map((c) => (
            <Leaf key={c.label} item={c} />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ collapsed, mobileOpen, onClose }: SidebarProps) {
  const cls = [
    'sidebar',
    collapsed ? 'sidebar--collapsed' : '',
    mobileOpen ? 'sidebar--mobile-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={cls}>
      <div className="sidebar__header">
        <Logo collapsed={collapsed} />
        <button type="button" className="sidebar__close" onClick={onClose} aria-label="Close menu">
          <CloseIcon />
        </button>
      </div>
      <nav className="sidebar__nav" aria-label="Primary">
        {NAV_SECTIONS.map((section, i) => (
          <div className="sidebar__section" key={section.title ?? `s${i}`}>
            {section.title && <div className="sidebar__section-title">{section.title}</div>}
            {section.items.map((item) =>
              item.children ? <Group key={item.label} item={item} /> : <Leaf key={item.label} item={item} />,
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
}
